// PowMixedFamilyToSinglePowerTask.js
// Zadanie: wyrażenie z liczb jednej "rodziny" (np. 2,4,8 lub 3,9,27 lub 5,25,125).
// W treści występują liczby 2/4/8, 3/9/27 lub 5/25/125 (jako zwykłe liczby),
// a uczeń ma sprowadzić cały zapis do jednej potęgi o podstawie 2, 3 albo 5.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const factorLatex = (num, exp) =>
  exp === 1 ? `${num}` : `${num}^{${exp}}`;

// Losowy zapis dzielenia: dwukropek lub kreska ułamkowa (50/50).
// Przy ułamku NIE dodajemy żadnych dodatkowych nawiasów.
// Przy dwukropku możemy opcjonalnie opakować licznik/mianownik.
const divisionLatex = (
  numeratorLatex,
  denominatorLatex,
  { wrapNumerator = false, wrapDenominator = false, useFraction = Math.random() < 0.5 } = {}
) => {

  if (useFraction) {
    // czytelny, duży ułamek w trybie inline
    return `\\dfrac{${numeratorLatex}}{${denominatorLatex}}`;
  }

  const numWrapped = wrapNumerator
    ? `\\big(${numeratorLatex}\\big)`
    : numeratorLatex;
  const denWrapped = wrapDenominator
    ? `\\big(${denominatorLatex}\\big)`
    : denominatorLatex;

  return `${numWrapped}\\,:\\,${denWrapped}`;
};

// Ostatni czynnik jest mnożnikiem całego ilorazu, a nie częścią mianownika.
export function divisionThenProductLatex(data, useFraction) {
  const { Xnum, Ynum, Znum, m, n, p, k } = data;
  const poweredRight = `\\big(${factorLatex(Ynum, n)}\\big)^{${k}}`;
  return divisionLatex(factorLatex(Xnum, m), poweredRight, { useFraction }) +
    `\\,\\cdot\\,${factorLatex(Znum, p)}`;
}

function displayedExpression(data) {
  // Naprawia także warianty zapisane przed poprawką, bez ponownego losowania.
  if (data.pattern !== "divParMul") return data.leftLatex;
  const useFraction = data.divisionForm
    ? data.divisionForm === "fraction"
    : data.leftLatex.includes("\\dfrac");
  return divisionThenProductLatex(data, useFraction);
}

// Parsowanie "a^k"
function parsePowerInput(raw) {
  if (!raw) return null;
  const m = raw.replace(/\s/g, "").match(/^(\d+)\^(\d+)$/);
  if (!m) return null;
  return { base: parseInt(m[1], 10), exp: parseInt(m[2], 10) };
}

export function generateTask() {
  // Wybieramy rodzinę: {2,4,8} lub {3,9,27} lub {5,25,125}.
  // (Nie używamy 4/8/9 jako "baz zadania" – one tylko jako liczby do rozłożenia).
  const familyBase = pick([2, 3, 5]);
  const fam =
    familyBase === 2
      ? { a: 2, a2: 4, a3: 8, r2: 2, r3: 3 }
      : familyBase === 3
      ? { a: 3, a2: 9, a3: 27, r2: 2, r3: 3 }
      : { a: 5, a2: 25, a3: 125, r2: 2, r3: 3 };

  // Wzorce: mieszamy mnożenie/dzielenie i "potęgę potęgi" na (a^2)^s lub (a^3)^t.
  const patterns = [
    "mulDiv", // X^m · Y^n : Z^p
    "parMulDiv", // (X^m · Y^n)^k : Z^p
    "divParMul", // X^m : (Y^n)^k · Z^p
  ];

  let tries = 0;
  while (tries++ < 300) {
    const pattern = pick(patterns);

    // losujemy, którą z {a, a2, a3} bierzemy w każdej pozycji
    const pickFactor = () =>
      pick([
        { num: fam.a, powMul: 1, tag: "a" }, // a = (a^1)
        { num: fam.a2, powMul: fam.r2, tag: "a2" }, // a^2
        { num: fam.a3, powMul: fam.r3, tag: "a3" }, // a^3
      ]);

    const X = pickFactor();
    const Y = pickFactor();
    const Z = pickFactor();

    // WARUNEK: w treści muszą się pojawić co najmniej DWIE RÓŻNE liczby z rodziny
    const distinctNums = new Set([X.num, Y.num, Z.num]).size;
    if (distinctNums < 2) {
      continue; // losujemy jeszcze raz
    }

    // wykładniki naturalne 1..9, k ∈ {2,3,4}
    const m = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    const n = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    const p = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    const k = pick([2, 3, 4]);

    let K = 0;
    let leftLatex = "";

    if (pattern === "mulDiv") {
      // X^m · Y^n : Z^p
      K = X.powMul * m + Y.powMul * n - Z.powMul * p;

      const numerator =
        `${factorLatex(X.num, m)}\\,\\cdot\\,${factorLatex(Y.num, n)}`;
      const denominator = `${factorLatex(Z.num, p)}`;

      // Przy dwukropku chcemy (licznik) : mianownik
      leftLatex = divisionLatex(numerator, denominator, {
        wrapNumerator: true,
        wrapDenominator: false,
      });
    }

    if (pattern === "parMulDiv") {
      // (X^m · Y^n)^k : Z^p
      K = (X.powMul * m + Y.powMul * n) * k - Z.powMul * p;

      const inside =
        `${factorLatex(X.num, m)}\\,\\cdot\\,${factorLatex(Y.num, n)}`;
      const numerator = `\\big(${inside}\\big)^{${k}}`;
      const denominator = `${factorLatex(Z.num, p)}`;

      // Tu zapis bez dodatkowego nawiasu wokół licznika jest czytelny
      leftLatex = divisionLatex(numerator, denominator, {
        wrapNumerator: false,
        wrapDenominator: false,
      });
    }

    if (pattern === "divParMul") {
      // X^m : (Y^n)^k · Z^p
      K = X.powMul * m - Y.powMul * n * k + Z.powMul * p;

      leftLatex = divisionThenProductLatex(
        { Xnum: X.num, Ynum: Y.num, Znum: Z.num, m, n, p, k },
        Math.random() < 0.5
      );
    }

    // wymagamy K ≥ 1 (brak zera i ujemnych)
    if (K >= 1) {
      return {
        type: "PowMixedFamilyToSinglePowerTask",
        data: {
          base: fam.a,
          leftLatex,
          divisionForm: leftLatex.includes("\\dfrac") ? "fraction" : "colon",
          K,
          familyBase,
          pattern,
          m,
          n,
          p,
          k,
          Xnum: X.num,
          Ynum: Y.num,
          Znum: Z.num,
          rX: X.powMul,
          rY: Y.powMul,
          rZ: Z.powMul,
        },
      };
    }
  }

  // awaryjnie – prosty przykład na rodzinie 2
  const base = 2;
  const K = 6;
  const leftLatex = `4^{2}\\,\\cdot\\,2^{3}\\,:\\,2`;
  return {
    type: "PowMixedFamilyToSinglePowerTask",
    data: {
      base,
      leftLatex,
      divisionForm: "colon",
      K,
      familyBase: 2,
      pattern: "mulDiv",
      m: 2,
      n: 3,
      p: 1,
      k: 1,
      Xnum: 4,
      Ynum: 2,
      Znum: 2,
      rX: 2,
      rY: 1,
      rZ: 1,
    },
  };
}

export function renderTask({ data }) {
  const { base } = data;
  const leftLatex = displayedExpression(data);

  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${base}`} />:
      </div>
      {/* wyrażenie na osobnej, wyśrodkowanej linijce */}
      <BlockMath math={leftLatex} />
    </div>
  );
}


export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 2^7"
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;

  const parsed = parsePowerInput(userAnswer.raw);
  if (!parsed) return false;

  const { base: correctBase, K: correctExp } = data;
  // Wymagamy dokładnie tej podstawy (2/3/5) i wykładnika K.
  return parsed.base === correctBase && parsed.exp === correctExp;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const raw = String(userAnswer.raw).trim().replace(/\s+/g, "");
  let latex = raw;

  const match = raw.match(/^(\d+)\^(\d+)$/);
  if (match) {
    latex = `${match[1]}^{${match[2]}}`;
  }

  return <InlineMath math={latex} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.base}^{${data.K}}`} />;
}

export function renderExplanation({ data }) {
  const {
    base,
    K,
    familyBase,
    pattern,
    m,
    n,
    p,
    k,
    Xnum,
    Ynum,
    Znum,
    rX,
    rY,
    rZ,
  } = data;
  const leftLatex = displayedExpression(data);

  // --- KROK 1: tylko te liczby z rodziny, które faktycznie wystąpiły ---
  const hintParts = [];
  const familyNumbers = new Set([Xnum, Ynum, Znum]);

  if (familyBase === 2) {
    if (familyNumbers.has(4)) hintParts.push("4=2^{2}");
    if (familyNumbers.has(8)) hintParts.push("8=2^{3}");
  } else if (familyBase === 3) {
    if (familyNumbers.has(9)) hintParts.push("9=3^{2}");
    if (familyNumbers.has(27)) hintParts.push("27=3^{3}");
  } else if (familyBase === 5) {
    if (familyNumbers.has(25)) hintParts.push("25=5^{2}");
    if (familyNumbers.has(125)) hintParts.push("125=5^{3}");
  }

  // gdyby z jakiegoś powodu nie było żadnej z powyższych liczb – wracamy do pełnego hintu
  let hintLatex;
  if (hintParts.length > 0) {
    hintLatex = hintParts.join(",\\;");
  } else {
    hintLatex =
      familyBase === 2
        ? "4=2^{2},\\;8=2^{3}"
        : familyBase === 3
        ? "9=3^{2},\\;27=3^{3}"
        : "25=5^{2},\\;125=5^{3}";
  }

  // --- KROK 3: szczegółowe liczenie wykładnika, zależnie od wzorca ---

  let step3Latex = "";

  if (pattern === "mulDiv") {
    // X^m · Y^n : Z^p
    const e1 = rX * m;
    const e2 = rY * n;
    const e3 = rZ * p;
    const sumSymbolic = `${e1}+${e2}-${e3}`;

    const basePow = (e) => `${base}^{${e}}`;

    step3Latex =
      `${leftLatex}` +
      `\\;=\\;${basePow(e1)}\\,\\cdot\\,${basePow(e2)}\\,:\\,${basePow(e3)}` +
      `\\;=\\;${base}^{${sumSymbolic}}` +
      `\\;=\\;${base}^{${K}}`;
  } else if (pattern === "parMulDiv") {
    // (X^m · Y^n)^k : Z^p
    const eInner1 = rX * m;
    const eInner2 = rY * n;
    const eInnerSum = eInner1 + eInner2;
    const eAfterK = eInnerSum * k;
    const eZ = rZ * p;

    step3Latex =
      `${leftLatex}` +
      `\\;=\\;\\big(${base}^{${eInner1}}\\,\\cdot\\,${base}^{${eInner2}}\\big)^{${k}}\\,:\\,${base}^{${eZ}}` +
      `\\;=\\;\\big(${base}^{${eInner1}+${eInner2}}\\big)^{${k}}\\,:\\,${base}^{${eZ}}` +
      `\\;=\\;${base}^{${eInnerSum}\\cdot${k}}\\,:\\,${base}^{${eZ}}` +
      `\\;=\\;${base}^{${eAfterK}-${eZ}}` +
      `\\;=\\;${base}^{${K}}`;
  } else if (pattern === "divParMul") {
    // X^m : (Y^n)^k · Z^p
    const eX = rX * m;
    const eY = rY * n * k;
    const eZ = rZ * p;
    const sumSymbolic = `${eX}+${eZ}-${eY}`;

    step3Latex =
      `${leftLatex}` +
      `\\;=\\;\\dfrac{${base}^{${eX}}\\,\\cdot\\,${base}^{${eZ}}}{${base}^{${eY}}}` +
      `\\;=\\;${base}^{${sumSymbolic}}` +
      `\\;=\\;${base}^{${K}}`;
  } else {
    // awaryjnie, gdyby z jakiegoś powodu pattern był inny
    step3Latex = `${leftLatex}\\;=\\;${base}^{${K}}`;
  }

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p>
        <strong>Krok 1:</strong> Zamień liczby z rodziny na potęgi wspólnej
        podstawy:
      </p>
      <BlockMath math={hintLatex} />

      <p>
        <strong>Krok 2:</strong> Zastosuj reguły:
      </p>
      <BlockMath math={"(a^{r})^{s}=a^{r\\cdot s}\\quad a^{m}\\cdot a^{n}=a^{m+n}\\quad a^{m}:a^{n}=a^{m-n}"} />

      <p>
        <strong>Krok 3:</strong> Zredukuj do jednej potęgi:
      </p>
      <BlockMath math={step3Latex} />
    </div>
  );
}
